import { useState } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useTechnology, useTechnologyProductFamilies } from '../../graphql/queries';
import {
  DetailSkeleton,
  EmptyState,
  StatusBadge,
  ErrorBoundary,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { TechnologyVersionDialog } from '../../components/technologies/TechnologyVersionDialog';
import { deleteTechnology, deleteTechnologyVersion, ApiError } from '../../lib/apiClient';
import { TechnologyLifecycleStage } from '../../graphql/types';
import type { TechnologyVersion } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';
import './technologyId.css';

export const Route = createFileRoute('/technologies/$technologyId')({
  component: TechnologyDetailPage,
});

export { TechnologyDetailPage };

function TechnologyDetailPage(): JSX.Element {
  const { technologyId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<TechnologyVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<TechnologyVersion | null>(null);
  const [isDeletingVersion, setIsDeletingVersion] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet — otherwise the
  // /technologies/$technologyId/edit child route never appears; this
  // component (its parent) is always rendered instead (issue #599).
  const isChildRoute = location.pathname.endsWith('/edit');

  const { data, loading, error: fetchError, refetch } = useTechnology(technologyId);
  const technology = data?.technology;
  const versions = technology?.versions ?? [];

  // Resolve the Product Family's display name for the deep link to its card
  // on /technology-families (issue #1537) — the reverse of UJ-TECH-1257's
  // family -> filtered-technologies link. Technology.productFamilyId has no
  // GraphQL field resolver for the family's name (checked
  // backend/src/graphql/resolvers/technology.ts), so it's resolved
  // client-side against the same family list the /technology-families page
  // already fetches.
  const { data: familiesData } = useTechnologyProductFamilies();
  const productFamily = familiesData?.technologyProductFamilies.find(
    (family) => family.entityId === technology?.productFamilyId
  );

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <div className="technology-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="technology-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load technology"
          description={fetchError.message}
          actionLabel="Back to Technologies"
          onAction={() =>
            void navigate({
              to: '/technologies',
              search: { page: 1, pageSize: 25, supplierId: '', productFamilyId: '' },
            })
          }
        />
      </div>
    );
  }

  if (!technology) {
    return (
      <div className="technology-detail-page">
        <EmptyState
          icon="search_off"
          title="Technology not found"
          description={`No technology found with ID: ${technologyId}`}
          actionLabel="Back to Technologies"
          onAction={() =>
            void navigate({
              to: '/technologies',
              search: { page: 1, pageSize: 25, supplierId: '', productFamilyId: '' },
            })
          }
        />
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isEolWarning =
    technology.lifecycleStage === TechnologyLifecycleStage.Hold ||
    technology.lifecycleStage === TechnologyLifecycleStage.Sunset;

  const handleEdit = (): void => {
    void navigate({
      to: '/technologies/$technologyId/edit',
      params: { technologyId: technology.entityId },
    });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteTechnology(technologyId, false);
      await apolloClient.refetchQueries({ include: ['ListTechnologies'] });
      void navigate({
        to: '/technologies',
        search: { page: 1, pageSize: 25, supplierId: '', productFamilyId: '' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleAddVersion = (): void => {
    setEditingVersion(null);
    setVersionDialogOpen(true);
  };

  const handleEditVersion = (v: TechnologyVersion): void => {
    setEditingVersion(v);
    setVersionDialogOpen(true);
  };

  const handleVersionDialogSuccess = (): void => {
    setVersionDialogOpen(false);
    setEditingVersion(null);
    void refetch();
  };

  const handleVersionDialogCancel = (): void => {
    setVersionDialogOpen(false);
    setEditingVersion(null);
  };

  const handleConfirmDeleteVersion = async (): Promise<void> => {
    if (!deletingVersion) return;
    setIsDeletingVersion(true);
    setVersionError(null);
    try {
      await deleteTechnologyVersion(technologyId, deletingVersion.entityId, false);
      setDeletingVersion(null);
      void refetch();
    } catch (err) {
      setVersionError(err instanceof ApiError ? err.message : 'Failed to delete version');
    } finally {
      setIsDeletingVersion(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="technology-detail-page">
        <div className="breadcrumb">
          <Link
            to="/technologies"
            search={{ page: 1, pageSize: 25, supplierId: '', productFamilyId: '' }}
            className="breadcrumb-link"
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            <span>Back to Technologies</span>
          </Link>
        </div>

        {error && (
          <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
            <div className="banner-content">
              <IconElement name="error" aria-hidden="true" />
              <span>{error}</span>
            </div>
          </BannerElement>
        )}

        {isEolWarning && (
          <BannerElement theme="warning" dismissed={false} className="eol-banner" role="alert">
            <div className="banner-content">
              <IconElement name="warning" aria-hidden="true" />
              <span>
                This technology is marked as <strong>{technology.lifecycleStage}</strong>. Plan
                migration or replacement accordingly.
              </span>
            </div>
          </BannerElement>
        )}

        <div className="technology-header">
          <div className="technology-header-content">
            <h1 className="forge-typography--headline4">{technology.name}</h1>
            {technology.version && (
              <p className="forge-typography--body2 technology-version">v{technology.version}</p>
            )}
          </div>
          <div className="technology-actions">
            <PermissionGate permission="technology:update">
              <ForgeButton type="button" variant="outlined" onClick={handleEdit}>
                Edit
              </ForgeButton>
            </PermissionGate>
            <PermissionGate permission="technology:delete">
              <ForgeButton
                type="button"
                variant="outlined"
                className="forge-button--danger"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </ForgeButton>
            </PermissionGate>
          </div>
        </div>

        <div className="technology-content">
          <CardElement className="technology-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Overview</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Product Family</span>
                <span className="detail-value">
                  {technology.productFamilyId ? (
                    productFamily ? (
                      <Link to="/technology-families" hash={productFamily.entityId}>
                        {productFamily.name}
                      </Link>
                    ) : (
                      technology.productFamilyId
                    )
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Lifecycle Stage</span>
                <span className="detail-value">
                  <StatusBadge variant="lifecycleStage" value={technology.lifecycleStage} />
                </span>
              </div>
              {technology.securityRating && (
                <div className="detail-row">
                  <span className="detail-label">Security Rating</span>
                  <span className="detail-value detail-value--capitalize">
                    {technology.securityRating}
                  </span>
                </div>
              )}
              {technology.description && (
                <div className="detail-row">
                  <span className="detail-label">Description</span>
                  <span className="detail-value">{technology.description}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Used By</span>
                <span className="detail-value">
                  {technology.usedByServiceCount == null ? (
                    '—'
                  ) : (
                    <Link
                      to="/services"
                      search={{
                        page: 1,
                        pageSize: 25,
                        search: '',
                        lifecycleStatus: '',
                        tier: '',
                        tagId: '',
                        teamId: '',
                        technologyId: technology.entityId,
                      }}
                      className="technology-used-by-link"
                    >
                      <IconElement name="link" aria-hidden="true" />
                      {technology.usedByServiceCount}{' '}
                      {technology.usedByServiceCount === 1 ? 'service' : 'services'}
                    </Link>
                  )}
                </span>
              </div>
            </div>
          </CardElement>

          <CardElement className="technology-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Vendor &amp; Support</h2>
            </div>
            <div className="card-content">
              {technology.supplierId && (
                <div className="detail-row">
                  <span className="detail-label">Supplier ID</span>
                  <span className="detail-value detail-value-code">{technology.supplierId}</span>
                </div>
              )}
              {technology.licenseType && (
                <div className="detail-row">
                  <span className="detail-label">License Type</span>
                  <span className="detail-value detail-value--capitalize">
                    {technology.licenseType}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Support End Date</span>
                <span className="detail-value">{formatDate(technology.supportEndDate)}</span>
              </div>
              {technology.replacesId && (
                <div className="detail-row">
                  <span className="detail-label">Replaces</span>
                  <span className="detail-value detail-value-code">{technology.replacesId}</span>
                </div>
              )}
              {technology.replacedById && (
                <div className="detail-row">
                  <span className="detail-label">Replaced By</span>
                  <span className="detail-value detail-value-code">{technology.replacedById}</span>
                </div>
              )}
            </div>
          </CardElement>

          <CardElement className="technology-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Metadata</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Entity ID</span>
                <span className="detail-value detail-value-code">{technology.entityId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(technology._createdAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created By</span>
                <span className="detail-value">{technology._createdBy}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">{formatDate(technology._updatedAt)}</span>
              </div>
              {technology.tags && technology.tags.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Tags</span>
                  <div className="detail-value tags-list">
                    {technology.tags.map((tag) => (
                      <span key={tag} className="tag-chip">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {technology.notes && (
                <div className="detail-row">
                  <span className="detail-label">Notes</span>
                  <span className="detail-value">{technology.notes}</span>
                </div>
              )}
            </div>
          </CardElement>

          <CardElement className="technology-card technology-versions-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Versions</h2>
              <PermissionGate permission="technology:update">
                <ForgeButton type="button" variant="outlined" onClick={handleAddVersion}>
                  Add Version
                </ForgeButton>
              </PermissionGate>
            </div>
            <div className="card-content">
              {versionError && (
                <BannerElement theme="error" dismissed={false} role="alert">
                  <span>{versionError}</span>
                </BannerElement>
              )}
              {versions.length === 0 ? (
                <p className="forge-typography--body2 technology-versions-empty">
                  No versions recorded yet.
                </p>
              ) : (
                <ul className="technology-versions-list">
                  {versions.map((v) => (
                    <li key={v.entityId} className="technology-versions-list__row">
                      <span className="technology-versions-list__label">{v.versionLabel}</span>
                      <StatusBadge variant="versionStatus" value={v.status} />
                      {v.releaseDate && (
                        <span className="technology-versions-list__date">
                          {formatDate(v.releaseDate)}
                        </span>
                      )}
                      <PermissionGate permission="technology:update">
                        <div className="technology-versions-list__actions">
                          <ForgeButton
                            type="button"
                            variant="text"
                            onClick={() => handleEditVersion(v)}
                            aria-label={`Edit version ${v.versionLabel}`}
                          >
                            Edit
                          </ForgeButton>
                          <ForgeButton
                            type="button"
                            variant="text"
                            className="forge-button--danger"
                            onClick={() => setDeletingVersion(v)}
                            aria-label={`Delete version ${v.versionLabel}`}
                          >
                            Delete
                          </ForgeButton>
                        </div>
                      </PermissionGate>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardElement>
        </div>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title="Delete Technology"
          message="Are you sure you want to delete this technology?"
          itemName={technology.name}
          warning="This action will soft delete the technology. It can be recovered by an administrator."
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setDeleteDialogOpen(false)}
          isDeleting={isDeleting}
        />

        <TechnologyVersionDialog
          open={versionDialogOpen}
          technologyId={technology.entityId}
          version={editingVersion}
          onSuccess={handleVersionDialogSuccess}
          onCancel={handleVersionDialogCancel}
        />

        <DeleteConfirmationDialog
          open={deletingVersion !== null}
          title="Delete Version"
          message="Are you sure you want to delete this version?"
          itemName={deletingVersion?.versionLabel ?? ''}
          warning="This action will soft delete the version. It can be recovered by an administrator."
          onConfirm={() => void handleConfirmDeleteVersion()}
          onCancel={() => setDeletingVersion(null)}
          isDeleting={isDeletingVersion}
        />
      </div>
    </ErrorBoundary>
  );
}
